<script setup>
// 颜色值计算器（nuxt#6 phase 2）。复刻 design 的 RGB↔CMYK / Grayscale→RGB Converter（纯前端，实时）。
import { ref, computed } from "vue";

const props = defineProps({ mode: { type: String, required: true } }); // rgb2cmyk | cmyk2rgb | gray2rgb

const clamp255 = (v) => Math.max(0, Math.min(255, parseInt(v, 10) || 0));
const clamp100 = (v) => Math.max(0, Math.min(100, parseInt(v, 10) || 0));
const toHex = (R, G, B) => "#" + [R, G, B].map((v) => clamp255(v).toString(16).padStart(2, "0")).join("").toUpperCase();
function rgbToCmyk(R, G, B) { R /= 255; G /= 255; B /= 255; const k = 1 - Math.max(R, G, B); if (k === 1) return [0, 0, 0, 100]; return [(1 - R - k) / (1 - k), (1 - G - k) / (1 - k), (1 - B - k) / (1 - k), k].map((v) => Math.round(v * 100)); }
function cmykToRgb(c, m, y, k) { c /= 100; m /= 100; y /= 100; k /= 100; return [255 * (1 - c) * (1 - k), 255 * (1 - m) * (1 - k), 255 * (1 - y) * (1 - k)].map((v) => Math.round(v)); }

// rgb2cmyk 模式输入
const r = ref(217), g = ref(119), b = ref(87);
const hexInput = ref("#D97757");
// cmyk2rgb 模式输入
const cIn = ref(0), mIn = ref(45), yIn = ref(60), kIn = ref(15);
// gray2rgb 模式输入
const gray = ref(128);

// 当前 RGB（驱动色块），随模式而定
const rgb = computed(() => {
  if (props.mode === "cmyk2rgb") return cmykToRgb(clamp100(cIn.value), clamp100(mIn.value), clamp100(yIn.value), clamp100(kIn.value));
  if (props.mode === "gray2rgb") { const v = clamp255(gray.value); return [v, v, v]; }
  return [clamp255(r.value), clamp255(g.value), clamp255(b.value)];
});
const hex = computed(() => toHex(rgb.value[0], rgb.value[1], rgb.value[2]));

const outRows = computed(() => {
  if (props.mode === "rgb2cmyk") { const [c, m, y, k] = rgbToCmyk(rgb.value[0], rgb.value[1], rgb.value[2]); return [["Cyan", c + "%"], ["Magenta", m + "%"], ["Yellow", y + "%"], ["Key (Black)", k + "%"]]; }
  return [["R", rgb.value[0]], ["G", rgb.value[1]], ["B", rgb.value[2]], ["HEX", hex.value]];
});
const copyText = computed(() => {
  if (props.mode === "rgb2cmyk") { const [c, m, y, k] = rgbToCmyk(rgb.value[0], rgb.value[1], rgb.value[2]); return `C:${c}% M:${m}% Y:${y}% K:${k}%`; }
  return `rgb(${rgb.value[0]}, ${rgb.value[1]}, ${rgb.value[2]})  ${hex.value}`;
});

// rgb2cmyk：number ↔ picker/hex 双向（显式处理，避免 watch 回环）
function afterRgb() { hexInput.value = toHex(clamp255(r.value), clamp255(g.value), clamp255(b.value)); }
function afterHex(val) { hexInput.value = val; const m = String(val).replace("#", "").trim(); if (/^[0-9a-fA-F]{6}$/.test(m)) { r.value = parseInt(m.slice(0, 2), 16); g.value = parseInt(m.slice(2, 4), 16); b.value = parseInt(m.slice(4, 6), 16); } }

const HEAD = { rgb2cmyk: ["RGB to CMYK", "CMYK output", "Copy CMYK"], cmyk2rgb: ["CMYK to RGB", "RGB output", "Copy RGB"], gray2rgb: ["Grayscale to RGB", "RGB / HEX output", "Copy RGB"] };
const labels = computed(() => HEAD[props.mode] || HEAD.rgb2cmyk);

const copied = ref(false);
async function copy() { try { if (navigator.clipboard) await navigator.clipboard.writeText(copyText.value); } catch (_) {} copied.value = true; setTimeout(() => (copied.value = false), 1400); }
</script>

<template>
  <div class="cv-wrap">
    <div class="cv-stage">
      <div class="cv-preview">
        <div class="cv-swatch" :style="{ background: `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` }"></div>
        <div class="cv-swatch-meta">{{ hex }}</div>
      </div>
      <div class="cv-panel">
        <!-- rgb2cmyk -->
        <template v-if="mode === 'rgb2cmyk'">
          <div class="cv-group">
            <label>Pick a color</label>
            <div class="cv-hexrow">
              <input type="color" :value="hexInput" @input="afterHex($event.target.value)" />
              <input type="text" :value="hexInput" @input="afterHex($event.target.value)" />
            </div>
          </div>
          <div class="cv-group">
            <label>RGB input</label>
            <div class="cv-inputs">
              <div class="cv-cell"><span>R</span><input type="number" min="0" max="255" v-model="r" @input="afterRgb" /></div>
              <div class="cv-cell"><span>G</span><input type="number" min="0" max="255" v-model="g" @input="afterRgb" /></div>
              <div class="cv-cell"><span>B</span><input type="number" min="0" max="255" v-model="b" @input="afterRgb" /></div>
            </div>
          </div>
        </template>
        <!-- cmyk2rgb -->
        <template v-else-if="mode === 'cmyk2rgb'">
          <div class="cv-group">
            <label>CMYK input (%)</label>
            <div class="cv-inputs">
              <div class="cv-cell"><span>C</span><input type="number" min="0" max="100" v-model="cIn" /></div>
              <div class="cv-cell"><span>M</span><input type="number" min="0" max="100" v-model="mIn" /></div>
              <div class="cv-cell"><span>Y</span><input type="number" min="0" max="100" v-model="yIn" /></div>
              <div class="cv-cell"><span>K</span><input type="number" min="0" max="100" v-model="kIn" /></div>
            </div>
          </div>
        </template>
        <!-- gray2rgb -->
        <template v-else>
          <div class="cv-group">
            <label>Grayscale value (0–255)</label>
            <div class="rangewrap">
              <input type="range" min="0" max="255" v-model="gray" style="flex: 1" />
              <span class="rangeval">{{ rgb[0] }}</span>
            </div>
          </div>
        </template>

        <div class="cv-group">
          <label>{{ labels[1] }}</label>
          <div class="cv-out">
            <div v-for="(row, i) in outRows" :key="i" class="row"><span class="k">{{ row[0] }}</span><span class="v">{{ row[1] }}</span></div>
          </div>
        </div>
        <button class="btn btn-outline" type="button" @click="copy">{{ copied ? "Copied!" : labels[2] }}</button>
      </div>
    </div>

    <div class="trust">
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Runs in your browser</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Instant conversion</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Free forever</span>
    </div>
  </div>
</template>

<style scoped>
.cv-stage { border: 1px solid var(--line); border-radius: 22px; background: var(--surface); overflow: hidden; display: grid; grid-template-columns: 1fr 1fr; }
.cv-preview { min-height: 300px; display: flex; flex-direction: column; }
.cv-swatch { flex: 1; min-height: 170px; }
.cv-swatch-meta { padding: 16px 20px; background: var(--surface-2); font-family: var(--font-h); font-weight: 600; font-size: 15px; text-align: center; }
.cv-panel { padding: 30px; border-left: 1px solid var(--line); display: flex; flex-direction: column; gap: 18px; }
.cv-group label { font-size: 12px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--tx-faint); display: block; margin-bottom: 10px; }
.cv-inputs { display: flex; gap: 10px; }
.cv-cell { flex: 1; display: flex; flex-direction: column; gap: 5px; }
.cv-cell span { font-size: 11px; font-weight: 700; color: var(--tx-dim); text-align: center; }
.cv-cell input { width: 100%; background: var(--surface-2); border: 1px solid var(--line); border-radius: 10px; padding: 11px 8px; color: var(--tx); font-family: var(--font-h); font-weight: 600; font-size: 16px; text-align: center; outline: none; }
.cv-cell input:focus { border-color: #f97316; }
.cv-out { background: #06070b; border: 1px solid var(--line); border-radius: 12px; padding: 16px 18px; }
.cv-out .row { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 7px 0; }
.cv-out .row + .row { border-top: 1px solid var(--line); }
.cv-out .k { font-size: 12px; font-weight: 700; color: var(--tx-faint); }
.cv-out .v { font-family: var(--font-h); font-weight: 700; font-size: 16px; color: #fdba74; }
.cv-hexrow { display: flex; align-items: center; gap: 10px; }
.cv-hexrow input[type="color"] { width: 46px; height: 46px; border: 1px solid var(--line); border-radius: 11px; background: none; cursor: pointer; padding: 3px; flex: none; }
.cv-hexrow input[type="text"] { flex: 1; background: var(--surface-2); border: 1px solid var(--line); border-radius: 11px; padding: 12px 14px; color: var(--tx); font-family: var(--font-h); font-weight: 600; font-size: 15px; outline: none; }
.rangewrap { display: flex; align-items: center; gap: 14px; }
.rangeval { color: #fdba74; min-width: 44px; text-align: right; font-family: var(--font-h); font-weight: 700; font-size: 16px; }
.trust { display: flex; gap: 22px; justify-content: center; flex-wrap: wrap; margin-top: 18px; }
.trust .i { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: var(--tx-dim); }
.trust .i svg { width: 16px; height: 16px; color: #f97316; }
@media (max-width: 760px) { .cv-stage { grid-template-columns: 1fr; } .cv-panel { border-left: none; border-top: 1px solid var(--line); } }
</style>
